import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import DatePicker from '@components/v2/form/DatePicker/DatePicker';
import TextField from '@components/v2/form/TextField/TextField';
import Button from '@components/v2/ui/Button/Button';
import { Box, Stack } from '@mui/material';
import { DateValidationError } from '@mui/x-date-pickers';
import { useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import useMeasuredWidth from '../../../../../../../../hooks/useMeasuredWidth';
import useMoney from '../../../../../../../../hooks/useMoney';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { MAX_DATE } from '../../../../../../../../utils/intervals';
import { OrderConditionsTestIds } from '../../../../../../../../utils/testIds/OrderConditions.testIds';
import VoucherRow from '../../../../../../../business-policies/components/VoucherRow/VoucherRow';
import { VOUCHER_LIST_WIDTH } from '../../../../../../../business-policies/components/VoucherRow/voucherRow.constants';
import { CommercialPolicyDTO } from '../../../../../../../commercial-policies/commercialPolicies.types';
import { UsedInOfflineMode } from '../../../../../../../offline/types/UsedInOfflineMode';
import { PaymentMethod } from '../../../../../../../payment-method/payment-method.types';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import OrderInvoicesButton from '../../../../../../conditions/components/OrderInvoicesButton';
import { OrderCondition } from '../../../../../../conditions/interfaces/orderCondition.interface';
import { OrderConditionBusinessPolicyVoucher } from '../../../../../../conditions/orderCondition.types';
import {
  getOrderConditionErrors,
  getOrderConditionFieldsWithError,
  hasMoreConditionsError,
} from '../../../../../../conditions/orderConditionFieldErrors.utils';
import {
  formatOrderConditionPaymentMethod,
  isPaymentMethodAvailable,
} from '../../../../../../conditions/orderConditionPaymentMethod.utils';
import { formatOrderConditionVoucher } from '../../../../../../conditions/orderConditionVoucher.utils';
import { useOrderContext } from '../../../../../../contexts/Order.context';
import { useOrderEditSectionsContext } from '../../../../../../edit/sections/OrderEditSections.context';
import { Order } from '../../../../../../order.types';
import useOrderConditionFieldsVisibility from '../../hooks/useOrderConditionFieldsVisibility';
import { CONDITION_GROUP_GAP } from '../../orderObjectConditionsTab.consts';
import OrderConditionApplyToAllModal from '../OrderConditionApplyToAllModal';
import OrderConditionSectionLabel from '../OrderConditionSectionLabel';
import {
  blurAutocompleteOnPointerSelect,
  getConditionFieldsGridSx,
  hasFieldError,
} from '../orderConditionDetails.utils';
import OrderConditionDeliveryFields from './components/OrderConditionDeliveryFields';
import OrderConditionPaymentMethodOption from './components/OrderConditionPaymentMethodOption/OrderConditionPaymentMethodOption';
import useOrderConditionDetailsForm from './hooks/useOrderConditionDetailsForm';

interface OrderConditionDetailsFormProps extends UsedInOfflineMode {
  order: Order;
  condition: OrderCondition;
  onOpenMoreConditions: () => void;
  onOpenOrderInvoices: () => void;
}

const OrderConditionDetailsForm = ({
  order,
  condition,
  isOfflineMode,
  onOpenMoreConditions,
  onOpenOrderInvoices,
}: OrderConditionDetailsFormProps) => {
  const { translate } = useTranslation();
  const { formatMoney } = useMoney();
  const { containerRef, width: containerWidth } = useMeasuredWidth();
  const { canEditOrder } = useOrderContext();
  const { rawConditionErrors } = useOrderEditSectionsContext();
  const [dateValidationError, setDateValidationError] = useState<DateValidationError>(null);
  const dateValidationErrorRef = useRef<DateValidationError>(null);

  const {
    formMethods: { control },
    save,
    applyToAll,
  } = useOrderConditionDetailsForm({ order, condition, isOfflineMode });

  const {
    availability,
    isDetailsSectionVisible,
    isBillingForecastDateVisible,
    isBillingForecastDateReadOnly,
    isVoucherVisible,
    isDeliverySectionVisible,
    isShippingCostVisible,
    isObservationVisible,
    isInternalObservationVisible,
    isMoreConditionsVisible,
    isOrderInvoicesVisible,
  } = useOrderConditionFieldsVisibility({ order, condition, isOfflineMode });

  const fieldsWithError = getOrderConditionFieldsWithError(rawConditionErrors, condition.id);
  const conditionErrors = getOrderConditionErrors(rawConditionErrors, condition.id);
  const customerId = order.customer?.id;

  const changeDateValidationError = (validationError: DateValidationError) => {
    dateValidationErrorRef.current = validationError;
    setDateValidationError(validationError);
  };

  const handleSave = () => {
    if (dateValidationErrorRef.current) {
      return;
    }

    save();
  };

  return (
    <Stack ref={containerRef} sx={{ gap: CONDITION_GROUP_GAP }}>
      {isDetailsSectionVisible && (
        <Stack sx={{ gap: 2 }}>
          <OrderConditionSectionLabel label={LangEnum.GLOBAL_DETAILS} />

          <Box sx={getConditionFieldsGridSx(isBillingForecastDateVisible, containerWidth)}>
            <Controller
              name="paymentId"
              control={control}
              render={({ field, fieldState }) => (
                <AsyncAutocomplete<PaymentMethod>
                  source={{
                    url: `orders/${order.id}/payment-methods/${condition.id}`,
                    limit: 20,
                    defaultQuery: { availability },
                  }}
                  label={translate(LangEnum.GLOBAL_PAYMENT_METHOD)}
                  value={field.value ?? null}
                  getOptionLabel={formatOrderConditionPaymentMethod}
                  initialData={condition.payment ? [condition.payment] : []}
                  disabled={!canEditOrder}
                  error={hasFieldError(fieldState, fieldsWithError, 'paymentId')}
                  helperText={fieldState.error?.message}
                  testId={OrderConditionsTestIds.ORDER_PAYMENT_METHOD}
                  onBlur={field.onBlur}
                  onClose={blurAutocompleteOnPointerSelect}
                  formatOptions={(options) => options.filter(isPaymentMethodAvailable)}
                  renderOption={(option) => (
                    <OrderConditionPaymentMethodOption paymentMethod={option} />
                  )}
                  onChange={(value) => {
                    field.onChange(value);
                    handleSave();
                  }}
                />
              )}
            />

            {isBillingForecastDateVisible && (
              <Controller
                name="billingForecastDate"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    disablePast
                    maxDate={MAX_DATE}
                    label={translate(LangEnum.ORDER_BILLING_FORECAST_DATE)}
                    value={field.value}
                    error={
                      !!dateValidationError ||
                      hasFieldError(fieldState, fieldsWithError, 'billingForecastDate')
                    }
                    helperText={
                      dateValidationError || fieldState.error
                        ? translate(LangEnum.GLOBAL_INVALID_DATE)
                        : undefined
                    }
                    disabled={!canEditOrder || isBillingForecastDateReadOnly}
                    onValidationErrorChange={changeDateValidationError}
                    onBlur={field.onBlur}
                    onChange={(value) => {
                      field.onChange(value);
                      handleSave();
                    }}
                  />
                )}
              />
            )}
          </Box>

          <Box sx={getConditionFieldsGridSx(isVoucherVisible, containerWidth)}>
            <Controller
              name="commercialPolicyId"
              control={control}
              render={({ field, fieldState }) => (
                <AsyncAutocomplete<CommercialPolicyDTO>
                  source={{
                    url: `orders/${order.id}/commercial-policies/${condition.id}`,
                    limit: 100,
                    defaultQuery: { availability },
                  }}
                  label={translate(LangEnum.GLOBAL_DISCOUNT)}
                  value={field.value ?? null}
                  getOptionLabel={(option) => `${option.name} - ${option.discount}%`}
                  formatOptions={(options) => options.filter((option) => option.availableToApply)}
                  initialData={condition.commercialPolicy ? [condition.commercialPolicy] : []}
                  disabled={!canEditOrder}
                  error={hasFieldError(fieldState, fieldsWithError, 'commercialPolicyId')}
                  helperText={fieldState.error?.message}
                  onBlur={field.onBlur}
                  onClose={blurAutocompleteOnPointerSelect}
                  onChange={(value) => {
                    field.onChange(value);
                    handleSave();
                  }}
                />
              )}
            />

            {isVoucherVisible && (
              <Controller
                name="voucherId"
                control={control}
                render={({ field, fieldState }) => (
                  <AsyncAutocomplete<OrderConditionBusinessPolicyVoucher>
                    source={{
                      url: 'business-policy-vouchers/available',
                      limit: 20,
                      defaultQuery: { orderId: order.id, orderConditionId: condition.id },
                    }}
                    label={translate(LangEnum.GLOBAL_VOUCHER)}
                    dropdownMinWidth={VOUCHER_LIST_WIDTH}
                    value={field.value ?? null}
                    getOptionLabel={(option) => formatOrderConditionVoucher(option, formatMoney)}
                    initialData={condition.businessPolicies.slice(0, 1)}
                    disabled={!canEditOrder}
                    error={hasFieldError(fieldState, fieldsWithError, 'voucherId')}
                    helperText={fieldState.error?.message}
                    testId={OrderConditionsTestIds.ORDER_VOUCHER}
                    onBlur={field.onBlur}
                    onClose={blurAutocompleteOnPointerSelect}
                    renderOption={(option) => <VoucherRow voucher={option} />}
                    onChange={(value) => {
                      field.onChange(value);
                      handleSave();
                    }}
                  />
                )}
              />
            )}
          </Box>
        </Stack>
      )}

      {isDeliverySectionVisible && (
        <OrderConditionDeliveryFields
          control={control}
          condition={condition}
          customerId={customerId}
          canEditOrder={canEditOrder}
          fieldsWithError={fieldsWithError}
          visibility={{ isShippingCostVisible }}
          onSave={handleSave}
        />
      )}

      <Stack sx={{ gap: 2 }}>
        {isInternalObservationVisible && (
          <Controller
            name="internalObservation"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                multiline
                rows={3}
                placeholder={translate(LangEnum.ORDER_CONDITIONS_INTERNAL_OBSERVATION_LABEL)}
                disabled={!canEditOrder}
                value={field.value ?? ''}
                onChange={field.onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onBlur={() => handleSave()}
              />
            )}
          />
        )}

        {isObservationVisible && (
          <Controller
            name="observation"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                multiline
                rows={3}
                placeholder={translate(LangEnum.ORDER_CONDITIONS_OBSERVATION_LABEL)}
                disabled={!canEditOrder}
                value={field.value ?? ''}
                onChange={field.onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onBlur={() => handleSave()}
              />
            )}
          />
        )}

        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {isMoreConditionsVisible && (
            <Button
              variant="contained"
              color="secondary"
              shape="square"
              disabled={!canEditOrder}
              onClick={onOpenMoreConditions}
              sx={{
                ...(hasMoreConditionsError(fieldsWithError, conditionErrors) && {
                  color: 'error.main',
                  borderColor: 'error.main',
                  '&:hover': { color: 'error.main' },
                }),
              }}
            >
              {translate(LangEnum.ORDER_CONDITION_SEE_MORE_CONDITION_LABEL)}
            </Button>
          )}

          {isOrderInvoicesVisible && (
            <OrderInvoicesButton hasInvoices={!!order.hasInvoices} onClick={onOpenOrderInvoices} />
          )}
        </Stack>
      </Stack>

      <OrderConditionApplyToAllModal {...applyToAll} />
    </Stack>
  );
};

export default OrderConditionDetailsForm;
